"use client"

import { usePathname } from "next/navigation"
import { Section } from "@/components/layout/Section"
import { Users, Target, Lightbulb, Award, CheckCircle2 } from "lucide-react"
import Image from "next/image"
import { CTA } from "@/components/sections/CTA"
import { type Locale } from "@/lib/i18n"
import { getTranslations } from "@/lib/translations"

export default function AboutPage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  const t = getTranslations(currentLocale)

  const policies = [
    {
      icon: Target,
      title: t.pages.about.sections.mission.title,
      badge: t.pages.about.sections.mission.badge,
      image: "/misyon.jpg",
      description: t.pages.about.sections.mission.description,
      principles: t.pages.about.sections.mission.principles,
    },
    {
      icon: Lightbulb,
      title: t.pages.about.sections.vision.title,
      badge: t.pages.about.sections.vision.badge,
      image: "/vizyon.jpg",
      description: t.pages.about.sections.vision.description,
      principles: t.pages.about.sections.vision.principles,
    },
    {
      icon: Users,
      title: t.pages.about.sections.values.title,
      badge: t.pages.about.sections.values.badge,
      image: "/degerler.jpg",
      description: t.pages.about.sections.values.description,
      principles: t.pages.about.sections.values.principles,
    },
    {
      icon: Award,
      title: t.pages.about.sections.experience.title,
      badge: t.pages.about.sections.experience.badge,
      image: "/basari.jpg",
      description: t.pages.about.sections.experience.description,
      principles: t.pages.about.sections.experience.principles,
    },
  ]
  
  return (
    <>
      <Section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-6">
            {t.pages.about.hero.title}
          </h1>
          <p className="text-lg text-white/90 sm:text-xl">
            {t.pages.about.hero.subtitle}
          </p>
        </div>
      </Section>

      <Section className="py-16 lg:py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto">
          {/* SEO Uyumlu Açıklama Metni */}
          <div className="mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-mono">
              {t.pages.about.sections.intro.title}
            </h2>
            <div className="space-y-6 text-base sm:text-lg text-slate-700 leading-relaxed">
              {t.pages.about.sections.intro.paragraphs.slice(0, 3).map((paragraph, index) => (
                <p key={index}>
                  {paragraph.split('LAKVERSE').map((part, i, arr) => {
                    if (i < arr.length - 1) {
                      return (
                        <span key={i}>
                          {part}
                          <strong className="text-[rgb(3,74,166)]">LAKVERSE</strong>
                        </span>
                      )
                    }
                    return <span key={i}>{part}</span>
                  })}
                </p>
              ))}
            </div>
          </div>

          {/* Kartlar - Grid Layout - Full Width */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 items-stretch">
            {policies.map((policy, index) => {
              const Icon = policy.icon
              
              return (
                <div key={index} className="bg-white overflow-hidden shadow-sm flex flex-col">
                  {/* Görsel - Üstte */}
                  <div className="relative h-[200px] bg-slate-100 flex-shrink-0">
                    <Image
                      src={policy.image}
                      alt={policy.title}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      quality={90}
                    />
                    <div className="absolute inset-0 bg-[rgb(3,74,166)]/30"></div>
                    
                    {/* Badge - Sol Alt */}
                    <div className="absolute bottom-4 left-4">
                      <span className="inline-block px-4 py-2 bg-[rgb(3,74,166)] text-white text-sm font-semibold rounded">
                        {policy.badge}
                      </span>
                    </div>
                    
                    {/* İkon - Sağ Üst */}
                    <div className="absolute top-4 right-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md">
                        <Icon className="h-6 w-6 text-[rgb(3,74,166)]" />
                      </div>
                    </div>
                  </div>

                  {/* İçerik - Altta */}
                  <div className="p-6 flex flex-col flex-1 min-h-0">
                    <h2 className="text-3xl font-bold mb-4 text-[rgb(3,74,166)] flex-shrink-0 min-h-[48px]">
                      {policy.title}
                    </h2>
                    
                    <div className="flex-1 flex flex-col min-h-0">
                      <p className="text-lg text-muted-foreground mb-6 h-[180px] overflow-y-auto">
                        {policy.description}
                      </p>

                      <div className="pt-6 border-t border-slate-200 flex-shrink-0">
                        <h3 className="text-xl font-bold mb-4 text-[rgb(3,74,166)]">{t.pages.about.sections.principlesTitle}</h3>
                        <ul className="space-y-3">
                          {policy.principles.map((principle, idx) => (
                            <li key={idx} className="flex items-start space-x-3">
                              <CheckCircle2 className="h-5 w-5 text-[rgb(3,74,166)] mt-0.5 flex-shrink-0" />
                              <span className="text-muted-foreground text-sm">{principle}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Önem Notu */}
          <div className="mt-12 p-6 bg-slate-50 border-l-4 border-[rgb(3,74,166)]">
            <h3 className="text-xl font-bold mb-3">{t.pages.about.sections.importance.title}</h3>
            <p className="text-muted-foreground">
              {t.pages.about.sections.importance.description}
            </p>
          </div>
        </div>
      </Section>
      <CTA locale={currentLocale} />
    </>
  )
}

